import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowLeft, MapPin, Wine, Leaf, ForkKnife, Globe, MagnifyingGlassPlus } from '@phosphor-icons/react';
import { Breadcrumb } from '../Product/Breadcrumb';

// Import region data functions
import { getProvenceData } from '../../data/regions/provence';
import { getBordeauxData } from '../../data/regions/bordeaux';
import { getRhoneData } from '../../data/regions/rhone';
import { getRiojaData } from '../../data/regions/rioja';
import { getRiberaDelDueroData } from '../../data/regions/ribera-del-duero';
import { getNavarraData } from '../../data/regions/navarra';
import { getSudOuestData } from '../../data/regions/sud-ouest';

// Import subregion data functions
import { getRiojaAltaData } from '../../data/subregions/rioja-alta';
import { getRiberaAltaData } from '../../data/subregions/ribera-alta';
import { getCimaCorgoData } from '../../data/subregions/cima-corgo';
import { getAlentejoCentralData } from '../../data/subregions/alentejo-central';
import { getSerraDaEstrelaData } from '../../data/subregions/serra-da-estrela';
import { getMoncaoMelgacoData } from '../../data/subregions/moncao-melgaco';
import { getChiantiClassicoData } from '../../data/subregions/chianti-classico';
import { getLangheData } from '../../data/subregions/langhe';
import { getValpolicellaData } from '../../data/subregions/valpolicella';
import { getBolgheriData } from '../../data/subregions/bolgheri';
import { getMaremmaData } from '../../data/subregions/maremma';
import { getArboisData } from '../../data/subregions/arbois';
import { getChateauChalonData } from '../../data/subregions/chateau-chalon';
import { getLEtoileData } from '../../data/subregions/l-etoile';
import { getWairauValleyData } from '../../data/subregions/wairau-valley';
import { getGimblettGravelsData } from '../../data/subregions/gimblett-gravels';
import { getBannockburnData } from '../../data/subregions/bannockburn';
import { getMartinboroughData } from '../../data/subregions/martinborough';
import { getGladstoneData } from '../../data/subregions/gladstone';
import { getMastertonData } from '../../data/subregions/masterton';
import { getWaiparaValleyData } from '../../data/subregions/waipara-valley';
import { getNorthCanterburyData } from '../../data/subregions/north-canterbury';
import { getCentralCanterburyData } from '../../data/subregions/central-canterbury';
import { getMoutereHillsData } from '../../data/subregions/moutere-hills';
import { getWaimeaPlainsData } from '../../data/subregions/waimea-plains';
import { getTasmanBayData } from '../../data/subregions/tasman-bay';
import { getSudsteiermarkData } from '../../data/subregions/sudsteiermark';
import { getWeststeiermarkData } from '../../data/subregions/weststeiermark';
import { getOststeiermarkData } from '../../data/subregions/oststeiermark';
import { getNussbergData } from '../../data/subregions/nussberg';
import { getBisambergData } from '../../data/subregions/bisamberg';
import { getMauerData } from '../../data/subregions/mauer';
import { getCahorsData } from '../../data/subregions/cahors';
import { getBergeracData } from '../../data/subregions/bergerac';
import { getMadiranData } from '../../data/subregions/madiran';
import { getJuranconData } from '../../data/subregions/jurancon';
import { getMedocData } from '../../data/subregions/medoc';
import { getSaintEmilionData } from '../../data/subregions/saint_emilion';
import { getGravesData } from '../../data/subregions/graves';
import { getBandolData } from '../../data/subregions/bandol';
import { getCotesProvenceData } from '../../data/subregions/cotes_provence';
import { getCassisData } from '../../data/subregions/cassis';
// Add missing subregion imports
import { getLenswoodData } from '../../data/subregions/lenswood';
import { getPenolaData } from '../../data/subregions/penola';
import { getClareData } from '../../data/subregions/clare';
import { getUpperYarraValleyData } from '../../data/subregions/upper-yarra-valley';
import { getBlewittSpringsData } from '../../data/subregions/blewitt-springs';
import { getWilyabrupData } from '../../data/subregions/wilyabrup';
import { getLowerHunterValleyData } from '../../data/subregions/lower-hunter-valley';
import { getHighEdenData } from '../../data/subregions/high-eden';
import { getEdenValleyData } from '../../data/subregions/eden-valley';

// Data mapping object - using functions
const regionDataMap = {
  'provence': getProvenceData,
  'bordeaux': getBordeauxData,
  'rhone': getRhoneData,
  'rioja': getRiojaData,
  'ribera-del-duero': getRiberaDelDueroData,
  'navarra': getNavarraData,
  'sud-ouest': getSudOuestData,
};

// Subregion data mapping
const subregionDataMap = {
  'rioja-alta': getRiojaAltaData,
  'ribera-alta': getRiberaAltaData,
  'cima-corgo': getCimaCorgoData,
  'alentejo-central': getAlentejoCentralData,
  'serra-da-estrela': getSerraDaEstrelaData,
  'moncao-melgaco': getMoncaoMelgacoData,
  'chianti-classico': getChiantiClassicoData,
  'langhe': getLangheData,
  'valpolicella': getValpolicellaData,
  'bolgheri': getBolgheriData,
  'maremma': getMaremmaData,
  'arbois': getArboisData,
  'chateau-chalon': getChateauChalonData,
  'l-etoile': getLEtoileData,
  'wairau-valley': getWairauValleyData,
  'gimblett-gravels': getGimblettGravelsData,
  'bannockburn': getBannockburnData,
  'martinborough': getMartinboroughData,
  'gladstone': getGladstoneData,
  'masterton': getMastertonData,
  'waipara-valley': getWaiparaValleyData,
  'north-canterbury': getNorthCanterburyData,
  'central-canterbury': getCentralCanterburyData,
  'moutere-hills': getMoutereHillsData,
  'waimea-plains': getWaimeaPlainsData,
  'tasman-bay': getTasmanBayData,
  'sudsteiermark': getSudsteiermarkData,
  'weststeiermark': getWeststeiermarkData,
  'oststeiermark': getOststeiermarkData,
  'nussberg': getNussbergData,
  'bisamberg': getBisambergData,
  'mauer': getMauerData,
  'cahors': getCahorsData,
  'bergerac': getBergeracData,
  'madiran': getMadiranData,
  'jurancon': getJuranconData,
  'medoc': getMedocData,
  'saint_emilion': getSaintEmilionData,
  'graves': getGravesData,
  'bandol': getBandolData,
  'cotes_provence': getCotesProvenceData,
  'cassis': getCassisData,
  // Add missing subregions to the map
  'lenswood': getLenswoodData,
  'penola': getPenolaData,
  'clare': getClareData,
  'upper-yarra-valley': getUpperYarraValleyData,
  'blewitt-springs': getBlewittSpringsData,
  'wilyabrup': getWilyabrupData,
  'lower-hunter-valley': getLowerHunterValleyData,
  'high-eden': getHighEdenData,
  'eden-valley': getEdenValleyData,
};

export const SubregionInfoPage = () => {
  const { country, region, subregion } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [subregionData, setSubregionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Normalize region parameter to handle accented characters
  const normalizeRegionParam = (regionParam) => {
    if (!regionParam) return null;
    
    // Convert accented characters to their non-accented equivalents
    const normalized = regionParam
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/[^a-z0-9]/g, '-') // Replace non-alphanumeric with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    
    return normalized;
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    
    // Handle both old and new URL structures
    let regionKey;
    if (region && country) {
      // New structure: /info/:country/regions/:region/:subregion
      regionKey = normalizeRegionParam(region);
    } else if (region) {
      // Old structure: /info/regions/:region/:subregion (fallback)
      regionKey = normalizeRegionParam(region);
    } else {
      setError('URL invalide');
      setLoading(false);
      return;
    }
    
    console.log('Debug - country:', country);
    console.log('Debug - region (original):', region);
    console.log('Debug - regionKey (normalized):', regionKey);
    console.log('Debug - subregion:', subregion);
    console.log('Debug - available keys:', Object.keys(regionDataMap));
    console.log('Debug - subregion keys:', Object.keys(subregionDataMap));
    
    // First, try to get standalone subregion data
    const getSubregionData = subregionDataMap[subregion];
    if (getSubregionData) {
      const subregionDataResult = getSubregionData();
      setSubregionData(subregionDataResult);
      
      // Get the parent region data for context
      const getRegionData = regionDataMap[regionKey];
      if (getRegionData) {
        setData(getRegionData());
      }
      
      setLoading(false);
      return;
    }
    
    // Fallback to old structure: subregion data within region data
    const getRegionData = regionDataMap[regionKey];
    
    if (getRegionData) {
      const regionData = getRegionData();
      setData(regionData);
      
      // Find the specific subregion
      const subregionInfo = regionData.subregions?.[subregion];
      if (subregionInfo) {
        setSubregionData(subregionInfo);
        setLoading(false);
      } else {
        // Show "no info available" message instead of redirecting to 404
        setError('no-info');
        setLoading(false);
      }
    } else {
      // Show "no info available" message instead of redirecting to 404
      setError('no-info');
      setLoading(false);
    }
  }, [country, region, subregion, navigate]);

  if (loading) {
    return <main style={{ padding: 40, textAlign: 'center' }}>Chargement...</main>;
  }
  
  if (error === 'no-info') {
    return (
      <main style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <Breadcrumb />
        
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px', paddingBottom: '16px', borderBottom: '1px solid #e8e0e8' }}>
          <button
            onClick={() => navigate(-1)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
            onMouseOver={e => e.currentTarget.style.background = '#f0e6f0'}
            onMouseOut={e => e.currentTarget.style.background = 'none'}
          >
            <ArrowLeft size={24} color="#4B164C" />
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '32px', color: '#4B164C', margin: '0 0 4px 0', fontWeight: '700' }}>{subregion}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#666', fontSize: '16px' }}>
              <MapPin size={16} />
              <span>{region} - {country}</span>
            </div>
          </div>
        </div>

        {/* No Info Available Message */}
        <section style={{ 
          background: '#f8f6fa', 
          borderRadius: '16px', 
          padding: '48px 32px', 
          boxShadow: '0 4px 24px rgba(75,22,76,0.08)',
          textAlign: 'center'
        }}>
          <div style={{ 
            width: '80px', 
            height: '80px', 
            background: '#4B164C', 
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px auto'
          }}>
            <MapPin size={32} color="#fff" />
          </div>
          <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600' }}>
            Informations non disponibles
          </h2>
          <p style={{ fontSize: '18px', lineHeight: '1.6', color: '#666', margin: '0 0 32px 0', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto' }}>
            Les informations pour l'appellation <strong>{subregion}</strong> ne sont pas encore disponibles dans notre base de données. 
            Nous travaillons continuellement à enrichir notre guide du vin.
          </p>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center' }}>
            <button 
              onClick={() => navigate(`/info/countries/${country}/regions/${region}`)}
              style={{
                background: '#4B164C',
                color: '#fff',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'background 0.2s'
              }}
              onMouseOver={e => e.currentTarget.style.background = '#6d2c7a'}
              onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
            >
              Explorer d'autres appellations
            </button>
            <button 
              onClick={() => navigate(`/info/countries/${country}/regions`)}
              style={{
                background: '#f0e6f0',
                color: '#4B164C',
                border: '1px solid #4B164C',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'background 0.2s'
              }}
              onMouseOver={e => e.currentTarget.style.background = '#e0d6e0'}
              onMouseOut={e => e.currentTarget.style.background = '#f0e6f0'}
            >
              Autres régions
            </button>
            <button 
              onClick={() => navigate(`/info/countries/${country}`)}
              style={{
                background: '#f0e6f0',
                color: '#4B164C',
                border: '1px solid #4B164C',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'background 0.2s'
              }}
              onMouseOver={e => e.currentTarget.style.background = '#e0d6e0'}
              onMouseOut={e => e.currentTarget.style.background = '#f0e6f0'}
            >
              Retour au pays
            </button>
          </div>
        </section>
      </main>
    );
  }
  
  if (error) {
    return (
      <main style={{ padding: 40, textAlign: 'center' }}>
        <h1>Erreur</h1>
        <p>Une erreur s'est produite lors du chargement des données.</p>
        <div style={{ margin: '2rem 0' }}>
          <button onClick={() => navigate(-1)} style={{ marginRight: 16, background: '#4B164C', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '16px' }}>Retour</button>
          <button onClick={() => navigate(`/info/countries/${country}/regions/${region}`)} style={{ marginRight: 16, background: '#f0e6f0', color: '#4B164C', border: '1px solid #4B164C', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '16px' }}>Explorer d'autres appellations</button>
          <button onClick={() => navigate('/')} style={{ background: '#f0e6f0', color: '#4B164C', border: '1px solid #4B164C', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '16px' }}>Accueil</button>
        </div>
      </main>
    );
  }
  if (!data || !subregionData) return null;

  return (
    <main style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <Breadcrumb />
      
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px', paddingBottom: '16px', borderBottom: '1px solid #e8e0e8' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
          onMouseOver={e => e.currentTarget.style.background = '#f0e6f0'}
          onMouseOut={e => e.currentTarget.style.background = 'none'}
        >
          <ArrowLeft size={24} color="#4B164C" />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '32px', color: '#4B164C', margin: '0 0 4px 0', fontWeight: '700' }}>{subregionData.name}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#666', fontSize: '16px' }}>
            <MapPin size={16} />
            <span>{data.name} - {data.country}</span>
          </div>
        </div>
        <button
          onClick={() => navigate(`/search?pays_origine=${encodeURIComponent(data.country)}&region_origine=${encodeURIComponent(data.name)}&sous_region_origine_1=${encodeURIComponent(subregionData.name)}`)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 20px',
            background: '#4B164C',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '500',
            transition: 'background 0.2s'
          }}
          onMouseOver={e => e.currentTarget.style.background = '#6d2c7a'}
          onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
        >
          <MagnifyingGlassPlus size={18} />
          Rechercher des vins
        </button>
      </div>

      {/* Main Content */}
      <div style={{ display: 'grid', gap: '32px' }}>
        {/* Description */}
        <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
          <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600' }}>Description</h2>
          <p style={{ fontSize: '18px', lineHeight: '1.6', color: '#333', margin: '0' }}>{subregionData.description}</p>
        </section>

        {/* Characteristics */}
        {subregionData.characteristics && (
          <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
            <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 24px 0', fontWeight: '600' }}>Caractéristiques</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px' }}>
              {Object.entries(subregionData.characteristics).map(([key, value]) => (
                <div key={key}>
                  <h3 style={{ fontSize: '18px', color: '#4B164C', margin: '0 0 12px 0', fontWeight: '600', textTransform: 'capitalize' }}>{key}</h3>
                  <p style={{ fontSize: '16px', color: '#333', margin: '0', lineHeight: '1.5' }}>{value}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Grape Varieties */}
        {subregionData.grapeVarieties && (
          <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Wine size={24} />
                Cépages Principaux
              </h2>
              <button
                onClick={() => navigate(`/info/countries/${country}/regions/${region}/${subregion}/cepages`)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  background: '#4B164C',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.background = '#6d2c7a'}
                onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
              >
                Voir tous les cépages
                <Wine size={14} />
              </button>
            </div>
            
            {/* Handle different grapeVarieties structures */}
            {Array.isArray(subregionData.grapeVarieties) ? (
              // If grapeVarieties is an array of objects
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
                {subregionData.grapeVarieties.map((variety, index) => (
                  <div key={index} style={{ background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e8e0e8' }}>
                    <h3 style={{ fontSize: '18px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600' }}>{variety.name}</h3>
                    <div style={{ marginBottom: '12px' }}>
                      <span style={{ 
                        background: '#4B164C', 
                        color: '#fff', 
                        padding: '4px 8px', 
                        borderRadius: '12px', 
                        fontSize: '12px', 
                        fontWeight: '500' 
                      }}>
                        {variety.percentage}
                      </span>
                    </div>
                    <p style={{ fontSize: '14px', color: '#333', margin: '0 0 12px 0', lineHeight: '1.5' }}>{variety.description}</p>
                    {variety.characteristics && (
                      <p style={{ fontSize: '14px', color: '#666', margin: '0', lineHeight: '1.4' }}>
                        <strong>Caractéristiques:</strong> {variety.characteristics}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : typeof subregionData.grapeVarieties === 'object' ? (
              // If grapeVarieties is an object with color keys
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
                {Object.entries(subregionData.grapeVarieties).map(([color, varieties]) => (
                  <div key={color} style={{ background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e8e0e8' }}>
                    <h3 style={{ fontSize: '18px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600', textTransform: 'capitalize' }}>{color}</h3>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {Array.isArray(varieties) && varieties.map((variety, index) => (
                        <span key={index} style={{ 
                          background: '#4B164C', 
                          color: '#fff', 
                          padding: '6px 12px', 
                          borderRadius: '20px', 
                          fontSize: '14px', 
                          fontWeight: '500' 
                        }}>
                          {variety}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Fallback for other structures
              <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e8e0e8' }}>
                <p style={{ fontSize: '16px', color: '#333', margin: '0', lineHeight: '1.5' }}>
                  Informations sur les cépages disponibles pour cette appellation.
                </p>
              </div>
            )}
          </section>
        )}

        {/* Wine Styles */}
        {subregionData.wineStyles && (
          <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
            <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 24px 0', fontWeight: '600' }}>Styles de Vins</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
              {Object.entries(subregionData.wineStyles).map(([style, description], index) => (
                <div key={index} style={{ background: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e8e0e8', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ background: '#4B164C', color: '#fff', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '600', flexShrink: 0, marginTop: '2px' }}>{index + 1}</div>
                  <div>
                    <h3 style={{ fontSize: '16px', color: '#4B164C', margin: '0 0 8px 0', fontWeight: '600', textTransform: 'capitalize' }}>{style}</h3>
                    <p style={{ fontSize: '14px', color: '#333', margin: '0', lineHeight: '1.5' }}>{description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Food Pairing */}
        {subregionData.foodPairing && (
          <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
            <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ForkKnife size={24} />
              Accords Mets & Vins
            </h2>
            <p style={{ fontSize: '18px', lineHeight: '1.6', color: '#333', margin: '0' }}>{subregionData.foodPairing}</p>
          </section>
        )}

        {/* Continue Exploring Navigation */}
        <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
          <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 24px 0', fontWeight: '600', textAlign: 'center' }}>
            Continuez à Explorer
          </h2>
          
          <div style={{ display: 'grid', gap: '24px' }}>
            {/* Grape Varieties Section */}
            {subregionData.grapeVarieties && (
              <div>
                <h3 style={{ fontSize: '20px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Wine size={20} />
                  Cépages de {subregionData.name}
                </h3>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  background: '#fff',
                  border: '2px solid #e8e0e8',
                  borderRadius: '12px',
                  padding: '20px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => navigate(`/info/countries/${country}/regions/${region}/${subregion}/cepages`)}
                onMouseOver={e => {
                  e.currentTarget.style.borderColor = '#4B164C';
                  e.currentTarget.style.background = '#f0e6f0';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.borderColor = '#e8e0e8';
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <div style={{ 
                    width: '50px', 
                    height: '50px', 
                    background: '#4B164C', 
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '20px'
                  }}>
                    🍇
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#4B164C', marginBottom: '4px' }}>
                      Découvrir les Cépages
                    </div>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                      Explorez tous les cépages cultivés dans cette appellation
                    </div>
                  </div>
                  <div style={{ fontSize: '24px', color: '#4B164C' }}>→</div>
                </div>
              </div>
            )}

            {/* Other Subregions Section */}
            {data.subregions && (
              <div>
                <h3 style={{ fontSize: '20px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MapPin size={20} />
                  Autres Appellations de {data.name}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
                  {Object.entries(data.subregions).filter(([key]) => key !== subregion).slice(0, 4).map(([key, otherSubregion]) => (
                    <button
                      key={key}
                      onClick={() => navigate(`/info/countries/${country}/regions/${region}/${key}`)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        background: '#fff',
                        border: '2px solid #e8e0e8',
                        borderRadius: '12px',
                        padding: '16px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        textAlign: 'left',
                        width: '100%'
                      }}
                      onMouseOver={e => {
                        e.currentTarget.style.borderColor = '#4B164C';
                        e.currentTarget.style.background = '#f0e6f0';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseOut={e => {
                        e.currentTarget.style.borderColor = '#e8e0e8';
                        e.currentTarget.style.background = '#fff';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <div style={{ 
                        width: '40px', 
                        height: '40px', 
                        background: '#4B164C', 
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}>
                        {otherSubregion.name.charAt(0)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#4B164C', marginBottom: '4px' }}>
                          {otherSubregion.name}
                        </div>
                        <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.4' }}>
                          {otherSubregion.description.substring(0, 80)}...
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '12px', 
              justifyContent: 'center',
              marginTop: '16px'
            }}>
              <button
                onClick={() => navigate(`/info/countries/${country}/regions/${region}`)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  background: '#6d2c7a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  transition: 'background 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.background = '#8a4a8a'}
                onMouseOut={e => e.currentTarget.style.background = '#6d2c7a'}
              >
                <MapPin size={18} />
                Retour à {data.name}
              </button>
              <button
                onClick={() => navigate(`/info/countries/${country}`)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  background: '#f0e6f0',
                  color: '#4B164C',
                  border: '2px solid #4B164C',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseOver={e => {
                  e.currentTarget.style.background = '#4B164C';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.background = '#f0e6f0';
                  e.currentTarget.style.color = '#4B164C';
                }}
              >
                <Globe size={18} />
                Retour à {data.country}
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}; 